import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button, Collapse, InputNumber, Switch, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faXmark } from "@fortawesome/free-solid-svg-icons"
import {
  BuilderDropZone,
  BuilderOperationToggle,
  BuilderStatsRow,
  BuilderQueryPreview,
} from "shared/ui/Builder"
import SignificanceControl from "shared/ui/Significance/SignificanceControl.component"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"
import { SIGNIFICANCE_TESTED_STATISTICS } from "types/significance.types"
import {
  BuilderSource,
  DataTile,
  ScriptSource,
  isDataTile,
} from "features/Dashboard/types/dashboardDocument"
import {
  findTile,
  useDashboardStore,
  useDashboardStoreApi,
} from "features/Dashboard/runtime/dashboardStore"
import {
  builderSourceToScriptText,
  KEEP_ONLY,
  leavesOfCodebook,
} from "features/Dashboard/runtime/tileScript"
import { planTileRun } from "features/Dashboard/runtime/planTileRun"
import ScriptEditor from "./ScriptEditor.component"

/** Script edits wait this long for typing to pause before the tile re-runs. */
export const SCRIPT_DEBOUNCE_MS = 600

const NO_STATS: STATISTIC_TYPE[] = []

/** A removed list as text; a keep-only list names what stays. */
const describeRemoved = (ids: string[]): string => {
  if (ids[0] === KEEP_ONLY) return `only ${ids.slice(1).join(", ")}`
  return ids.length > 0 ? ids.join(", ") : "none"
}

const isTransposed = (tile: DataTile): boolean =>
  tile.type === "table" ? tile.visual.transposed : false

/**
 * The tile's opt-out of the dashboard weight, shown only while the
 * dashboard has one. The tile runs again when it changes.
 */
const UnweightedField: React.FC<{ tile: DataTile; pageId: string }> = ({
  tile,
  pageId,
}) => {
  const weight = useDashboardStore((s) => s.document.data.weight)
  const updateTile = useDashboardStore((s) => s.updateTile)
  if (!weight) return null
  return (
    <div className="dq-field">
      <label className="dq-field dq-field--row">
        <span>Unweighted</span>
        <Switch
          size="small"
          checked={tile.unweighted}
          onChange={(unweighted) =>
            updateTile(pageId, tile.id, (t) =>
              isDataTile(t) ? { ...t, unweighted } : t
            )
          }
        />
      </label>
      <span className="dq-field__hint">
        Runs without the dashboard weight, {weight}.
      </span>
    </div>
  )
}

/** A script tile: the editor, which output to draw, and what last ran. */
const ScriptPanel: React.FC<{
  tile: DataTile
  source: ScriptSource
  pageId: string
}> = ({ tile, source, pageId }) => {
  const store = useDashboardStoreApi()
  const updateTile = useDashboardStore((s) => s.updateTile)
  const retryTile = useDashboardStore((s) => s.retryTile)
  const lastRun = useDashboardStore((s) => s.tiles[tile.id]?.script)
  const prefix = useDashboardStore((s) => {
    const plan = planTileRun(s, tile.id)
    return plan?.universe && plan.emitPrefix ? plan.universe.prefix : ""
  })
  const [draft, setDraft] = useState(source.script)
  const draftRef = useRef(source.script)
  // The script this panel last wrote, so its own write is not taken for an
  // outside change when it comes back through the store.
  const written = useRef(source.script)
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null)

  const write = (text: string): void => {
    if (text === written.current) return
    written.current = text
    // The tile may have been deleted while an edit was waiting.
    if (!findTile(store.getState(), tile.id)) return
    updateTile(pageId, tile.id, (t) =>
      isDataTile(t) && t.source.kind === "script"
        ? { ...t, source: { ...t.source, script: text } }
        : t
    )
  }
  /** Writes a waiting edit now; true when there was one. */
  const flush = (): boolean => {
    if (!pending.current) return false
    clearTimeout(pending.current)
    pending.current = null
    const changed = draftRef.current !== written.current
    write(draftRef.current)
    return changed
  }
  const flushRef = useRef(flush)
  flushRef.current = flush
  // Leaving the tile (another selection, View mode) keeps the last keystrokes.
  useEffect(() => () => void flushRef.current(), [])

  useEffect(() => {
    if (source.script === written.current) return
    // An outside change wins over an edit still waiting to be written.
    if (pending.current) {
      clearTimeout(pending.current)
      pending.current = null
    }
    written.current = source.script
    draftRef.current = source.script
    setDraft(source.script)
  }, [source.script])

  const onScriptChange = (text: string): void => {
    setDraft(text)
    draftRef.current = text
    if (pending.current) clearTimeout(pending.current)
    pending.current = setTimeout(() => {
      pending.current = null
      write(text)
    }, SCRIPT_DEBOUNCE_MS)
  }
  // A waiting edit re-runs the tile as it is written; otherwise run again.
  const run = (): void => {
    if (!flush()) retryTile(tile.id)
  }

  return (
    <div className="dq-inspector__section">
      <ScriptEditor
        value={draft}
        onChange={onScriptChange}
        onRun={run}
        prefixPreview={prefix}
      />
      {source.conditions.length > 0 && (
        <div className="dq-field">
          <span>Base</span>
          <div
            className="dq-script__universe"
            role="list"
            aria-label="Base (read only)"
          >
            {source.conditions.map((group, i) => (
              <div key={i} role="listitem">
                Any of: {group.join(", ")}
              </div>
            ))}
          </div>
          <span className="dq-field__hint">
            Kept from the builder. The lines combine with AND. It applies where
            the script has {"{{universe}}"}, together with the filters.
          </span>
        </div>
      )}
      <div className="dq-field">
        <span>Output to show</span>
        <InputNumber
          size="small"
          aria-label="Output to show"
          min={1}
          precision={0}
          value={source.outputIndex + 1}
          onChange={(v) => {
            if (typeof v !== "number" || v < 1) return
            updateTile(pageId, tile.id, (t) =>
              isDataTile(t) && t.source.kind === "script"
                ? { ...t, source: { ...t.source, outputIndex: v - 1 } }
                : t
            )
          }}
        />
        <span className="dq-field__hint">
          Which output to draw when the script produces more than one. 1 is the
          first.
        </span>
      </div>
      <UnweightedField tile={tile} pageId={pageId} />
      {lastRun && <BuilderQueryPreview script={lastRun} isIncomplete={false} />}
    </div>
  )
}

interface BaseGroup {
  id: number
  paths: string[]
}

/**
 * The tile's base: condition groups, each a drop zone whose drops combine
 * with OR, the groups combining with AND. A group with no drops yet lives
 * only here while it is being filled; the tile keeps the groups that have
 * drops.
 */
const BaseConditions: React.FC<{
  conditions: string[][]
  onChange: (next: string[][]) => void
}> = ({ conditions, onChange }) => {
  const nextId = useRef(0)
  const [groups, setGroups] = useState<BaseGroup[]>(() =>
    conditions.map((paths) => ({ id: nextId.current++, paths }))
  )
  // A parent dropped on a group lands as several drops in one event, so
  // each edit builds on the latest groups rather than the last render's.
  const groupsRef = useRef(groups)
  // What this section last wrote, so its own write is not taken for an
  // outside change when it comes back through the store.
  const written = useRef(JSON.stringify(conditions))
  const conditionsKey = JSON.stringify(conditions)

  useEffect(() => {
    if (conditionsKey === written.current) return
    written.current = conditionsKey
    const next = (JSON.parse(conditionsKey) as string[][]).map((paths) => ({
      id: nextId.current++,
      paths,
    }))
    groupsRef.current = next
    setGroups(next)
  }, [conditionsKey])

  const edit = (fn: (gs: BaseGroup[]) => BaseGroup[]): void => {
    const next = fn(groupsRef.current)
    groupsRef.current = next
    setGroups(next)
    const saved = next.filter((g) => g.paths.length > 0).map((g) => g.paths)
    const text = JSON.stringify(saved)
    if (text === written.current) return
    written.current = text
    onChange(saved)
  }
  const addGroup = (): void =>
    edit((gs) => [...gs, { id: nextId.current++, paths: [] }])
  const removeGroup = (id: number) => (): void =>
    edit((gs) => gs.filter((g) => g.id !== id))
  const addPath =
    (id: number) =>
    (path: string): void =>
      edit((gs) =>
        gs.map((g) =>
          g.id === id && !g.paths.includes(path)
            ? { ...g, paths: [...g.paths, path] }
            : g
        )
      )
  const removePath =
    (id: number) =>
    (path: string): void =>
      edit((gs) =>
        gs.map((g) =>
          g.id === id ? { ...g, paths: g.paths.filter((p) => p !== path) } : g
        )
      )

  return (
    <div className="qc-conditions">
      {groups.map((g, i) => (
        <React.Fragment key={g.id}>
          {i > 0 && (
            <div className="qc-conditions__connector" aria-hidden>
              AND
            </div>
          )}
          <div className="qc-conditions__group">
            <Tooltip title="Remove condition group" placement="top">
              <button
                type="button"
                className="qc-conditions__remove"
                aria-label={`Remove condition group ${i + 1}`}
                onClick={removeGroup(g.id)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </Tooltip>
            <BuilderDropZone
              label="Any of"
              ariaLabel={`Any of, group ${i + 1}`}
              variables={g.paths}
              emptyHint="Drop a variable or categories here"
              onAdd={addPath(g.id)}
              onRemove={removePath(g.id)}
              expandParents
            />
          </div>
        </React.Fragment>
      ))}
      <Button
        type="dashed"
        block={groups.length === 0}
        className={groups.length === 0 ? undefined : "self-start"}
        icon={<FontAwesomeIcon icon={faPlus} />}
        onClick={addGroup}
      >
        Add condition group
      </Button>
    </div>
  )
}

/** A builder tile: the console's builder parts, bound to the tile source. */
const BuilderPanel: React.FC<{
  tile: DataTile
  source: BuilderSource
  pageId: string
}> = ({ tile, source: src, pageId }) => {
  const codebook = useDashboardStore((s) => s.codebook)
  const updateTile = useDashboardStore((s) => s.updateTile)
  const [confirmSwitch, setConfirmSwitch] = useState(false)
  const leavesOf = useMemo(() => leavesOfCodebook(codebook), [codebook])

  const setSource = (fn: (s: BuilderSource) => BuilderSource): void =>
    updateTile(pageId, tile.id, (t) =>
      isDataTile(t) && t.source.kind === "builder"
        ? { ...t, source: fn(t.source) }
        : t
    )

  const hasRows = src.rows.length > 0
  const preview = hasRows
    ? builderSourceToScriptText({
        tileId: tile.id,
        source: src,
        transposed: isTransposed(tile),
        leavesOf,
      })
    : ""
  const lockedStats = src.significance?.enabled
    ? SIGNIFICANCE_TESTED_STATISTICS[src.operation]
    : NO_STATS
  const toggleStat = (op: STATISTIC_TYPE): void =>
    setSource((s) => ({
      ...s,
      statistics: s.statistics.includes(op)
        ? s.statistics.filter((o) => o !== op)
        : [...s.statistics, op],
    }))
  const addTo =
    (axis: "rows" | "columns") =>
    (v: string): void => {
      if (!src[axis].includes(v)) {
        setSource((s) => ({ ...s, [axis]: [...s[axis], v] }))
      }
    }
  const removeFrom =
    (axis: "rows" | "columns") =>
    (v: string): void =>
      setSource((s) => ({ ...s, [axis]: s[axis].filter((x) => x !== v) }))
  // A DTAB table has no headers to remove (see TableTile), so it shows the
  // lists only when an earlier XTAB left some behind.
  const showRemoved =
    (tile.type === "table" && src.operation !== TABLE_OPERATION_TYPE.DTAB) ||
    src.removed.rows.length > 0 ||
    src.removed.columns.length > 0

  return (
    <div className="dq-inspector__section dq-inspector__section--builder">
      <BuilderOperationToggle
        value={src.operation}
        onChange={(operation) => {
          if (operation === src.operation) return
          setSource((s) => ({
            ...s,
            operation,
            statistics: [
              operation === TABLE_OPERATION_TYPE.DTAB
                ? STATISTIC_TYPE.AVG
                : STATISTIC_TYPE.COL_PERCENT,
            ],
          }))
        }}
      />
      <BuilderDropZone
        label="Rows"
        variables={src.rows}
        emptyHint="Drop a variable here"
        onAdd={addTo("rows")}
        onRemove={removeFrom("rows")}
      />
      <BuilderDropZone
        label="Columns"
        variables={src.columns}
        emptyHint="Totals only"
        onAdd={addTo("columns")}
        onRemove={removeFrom("columns")}
      />
      <div className="dq-field">
        <span>Statistics</span>
        <BuilderStatsRow
          operation={src.operation}
          selectedOperators={src.statistics}
          onToggle={toggleStat}
          lockedOperators={lockedStats}
        />
      </div>
      {/* Not <label>: a click on the caption would press the first segment. */}
      <div className="dq-field">
        <span>Significance</span>
        <SignificanceControl
          value={src.significance}
          onChange={(significance) => setSource((s) => ({ ...s, significance }))}
          tableType={src.operation}
        />
      </div>
      <div className="dq-field">
        <span>Base</span>
        <BaseConditions
          conditions={src.conditions}
          onChange={(conditions) => setSource((s) => ({ ...s, conditions }))}
        />
        <span className="dq-field__hint">
          Groups combine with AND; drops inside a group combine with OR.
        </span>
      </div>
      <UnweightedField tile={tile} pageId={pageId} />
      {showRemoved && (
        <Collapse
          size="small"
          ghost
          className="dq-inspector__advanced"
          items={[
            {
              key: "adv",
              label: "Advanced",
              children: (
                <div className="dq-removed">
                  <div className="dq-removed__row">
                    <span className="dq-removed__name">Removed rows</span>
                    <span className="dq-removed__list">
                      {describeRemoved(src.removed.rows)}
                    </span>
                    {src.removed.rows.length > 0 && (
                      <DataQButton
                        type="secondary"
                        size="small"
                        onClick={() =>
                          setSource((s) => ({
                            ...s,
                            removed: { ...s.removed, rows: [] },
                          }))
                        }
                      >
                        Clear
                      </DataQButton>
                    )}
                  </div>
                  <div className="dq-removed__row">
                    <span className="dq-removed__name">Removed columns</span>
                    <span className="dq-removed__list">
                      {describeRemoved(src.removed.columns)}
                    </span>
                    {src.removed.columns.length > 0 && (
                      <DataQButton
                        type="secondary"
                        size="small"
                        onClick={() =>
                          setSource((s) => ({
                            ...s,
                            removed: { ...s.removed, columns: [] },
                          }))
                        }
                      >
                        Clear
                      </DataQButton>
                    )}
                  </div>
                  <p className="dq-field__hint">
                    Select headers on the table tile and use Remove selected to
                    add to these lists.
                  </p>
                </div>
              ),
            },
          ]}
        />
      )}
      <BuilderQueryPreview script={preview} isIncomplete={!hasRows} />
      <div className="dq-inspector__footer">
        <DataQButton
          type="secondary"
          size="small"
          onClick={() => setConfirmSwitch(true)}
          isDisabled={!hasRows}
        >
          Switch to script
        </DataQButton>
      </div>
      <ConfirmationModal
        title="Switch to script"
        isVisible={confirmSwitch}
        message="The builder settings become an editable script. This cannot be switched back."
        confirmButtonText="Switch"
        cancel={() => setConfirmSwitch(false)}
        confirm={() => {
          updateTile(pageId, tile.id, (t) => {
            if (!isDataTile(t) || t.source.kind !== "builder") return t
            return {
              ...t,
              source: {
                kind: "script",
                script: builderSourceToScriptText({
                  tileId: t.id,
                  source: t.source,
                  transposed: isTransposed(t),
                  leavesOf,
                }),
                outputIndex: 0,
                // The {{universe}} token stands for the filters and this.
                conditions: t.source.conditions,
              },
            }
          })
          setConfirmSwitch(false)
        }}
      />
    </div>
  )
}

/**
 * What a data tile runs: the builder (rows, columns, statistics,
 * significance, its own base) with the script it generates, or, after
 * "Switch to script", the script itself; and, while the dashboard has a
 * weight, whether the tile leaves it out. Every edit goes through
 * `updateTile`, which marks the tile stale so it re-runs.
 */
const DataTab: React.FC<{ tile: DataTile; pageId: string }> = ({
  tile,
  pageId,
}) =>
  tile.source.kind === "script" ? (
    <ScriptPanel tile={tile} source={tile.source} pageId={pageId} />
  ) : (
    <BuilderPanel tile={tile} source={tile.source} pageId={pageId} />
  )

export default DataTab
